import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Head from 'next/head';
import directory from '@tutorbot/directory-api';
import { Element } from 'react-scroll';
import Router from '../../routes';
import { LocalizationContext } from '../../hoc/Localization';
import { LocationContext } from '../../hoc/Location';
import { EditorContext } from '../../hoc/Editor';
import { RouteContext } from '../../hoc/Route';
import { UserContext } from '../../hoc/User';
import { getCourseByShortId } from '../../utils/directory';
import { orderCourseByShortIdAndPaymentMethod } from '../../utils/api';
import { cleanString } from '../../utils';
import TitleContent from '../../components/TinyComponents/TitleContent';
import Register from '../../components/CourseComponents/Register';
import Header from '../../components/CourseComponents/Header';
import CardListHeader from '../../components/CardList/Header';
import Info from '../../components/CourseComponents/Info';
import Container from '../../components/Home/Container';
import JsonLD from '../../components/JsonLD/Course';
import CardList from '../../components/CardList';
import Loading from '../../components/Loading';
import useOnScreen from '../../components/useOnScreen';
import Picture from '../../components/Picture';
import styles from './styles.less';
import InlineEditor from '../../components/InlineEditor';
import Schedule from '../../components/CourseComponents/Schedule';

const ToolBar = dynamic(() => import('../../components/InlineEditor/ToolBar'));
const SchoolInfo = dynamic(() => import('../../components/SchoolInfo'));
const Blog = dynamic(() => import('../../components/SchoolComponents/Blog'));
const Reviews = dynamic(() => import('../../components/Reviews'));
const Map = dynamic(() => import('../../components/CustomMap'));
const Error = dynamic(() => import('../_error'));
const VideoPlayer = dynamic(() => import('../../components/VideoPlayer'));

const Course = ({ datas, load, create }) => {
    const { locale, t, translate } = React.useContext(LocalizationContext);
    const { locations } = React.useContext(LocationContext);
    const {
        params: { id, course: courseName },
    } = React.useContext(RouteContext);

    const { setEditMode, defineContent, edited, setType, editMode, saving } = React.useContext(
        EditorContext,
    );
    const { user } = React.useContext(UserContext);

    const [selectedSchedule, setSelectedSchedule] = React.useState({});
    const [orderLoading, setOrderLoading] = React.useState(false);
    const [register, setRegister] = React.useState(true);
    const [shareUrl, setShareUrl] = React.useState(null);
    const [reviews, setReviews] = React.useState([]);
    const [blog, setBlog] = React.useState({});
    const [courseRaw, setCourse] = React.useState(datas.course || {});
    const [relatedCourses, setRelatedCourses] = React.useState(datas.relatedCourses || []);
    const [otherCourses, setOtherCourses] = React.useState(datas.courses || []);
    const [loading, setLoading] = React.useState(load);
    const [key, setKey] = React.useState(Date.now());
    const [mapShow, setMapShow] = React.useState();
    const [videoLoading, setVideoLoading] = React.useState(true);

    const data = datas.course;

    const firstRef = React.useRef();

    const countries = locations
        .filter(location => location.default)
        .map(location => location.country);

    React.useEffect(() => {
        if (!user) {
            Router.pushRoute('courseShow', {
                locale,
                course: data.link,
                school: data.school.shortId,
                id: data.shortId,
                reference: 'updateCourse',
            });
        }

        setType('course');

        setShareUrl(window.location.href);

        if (!load || (data && data.shortId === id)) {
            if (data && data.schedules && data.schedules.length) {
                const index = data.schedules.length - 1;
                setSelectedSchedule(data.schedules[index]);
            }

            if (!create) {
                defineContent(data);
            }

            setKey(Date.now());
            return;
        }

        setLoading(true);

        if (courseName !== 'created') {
            setEditMode(true);
        }

        if (courseRaw.shortId !== id) {
            getCourseByShortId(id, {
                countries,
            }).then(res => {
                if (res) {
                    const {
                        course,
                        relatedCourses: fetchedRelatedCourses,
                        courses: fetchedOtherCourses,
                        reviews: fetchedReviews,
                    } = res;
                    // setEditMode(false);
                    setReviews(fetchedReviews);
                    setCourse(course);
                    setRelatedCourses(fetchedRelatedCourses);
                    setOtherCourses(fetchedOtherCourses);
                    if (course) {
                        const index = course.schedules.length - 1;
                        setSelectedSchedule(course.schedules[index]);
                    }
                    setLoading(false);
                    if (!create) {
                        defineContent(course);
                    }
                    setKey(Date.now());
                }
            });
        } else {
            setLoading(false);
        }
    }, [id]);

    React.useEffect(() => {
        if ((edited && Object.keys(edited).length && editMode) || create) {
            setCourse(edited);
        }
    }, [edited, editMode]);

    React.useEffect(() => {
        if (!editMode) {
            setEditMode(true);
        }
    }, []);

    React.useEffect(() => {
        if (courseRaw && courseRaw.id && courseRaw.school && courseRaw.school.blogShortId) {
            (async () => {
                setBlog(
                    (await directory().blog.where('shortId', courseRaw.school.blogShortId).get())
                        .data[0],
                );
            })();
        }
    }, [courseRaw && courseRaw.school && courseRaw.school.external_id]);

    const showMap = useOnScreen(firstRef, '150px');

    if (!mapShow && showMap) {
        setMapShow(true);
    }

    const canEdit =
        courseRaw &&
        user &&
        (create ||
            user.isAdmin ||
            (user.schools &&
                user.schools.length &&
                !!user.schools.find(s => courseRaw.school && s.id === courseRaw.school.id)));

    if (loading) {
        return <Loading full />;
    }

    if ((!courseRaw || (courseRaw && !courseRaw.shortId)) && !create) {
        return <Error />;
    }

    const orderCourse = async payment => {
        setOrderLoading(payment);

        try {
            const order = await orderCourseByShortIdAndPaymentMethod(courseRaw.shortId);

            if (order.redirect) {
                window.location = order.redirect;
            }
        } catch (e) {
            setOrderLoading(false);
        }
    };

    const course = {
        school: {},
        category: {},
        language: {},
        location: {},
        ...courseRaw,
        title: courseRaw.title,
        description: translate(courseRaw, 'description'),
        content: translate(courseRaw, 'content'),
        learn: translate(courseRaw, 'learn'),
    };

    const {
        title,
        learn,
        instructions,
        school,
        skills,
        perks,
        content,
        location,
        schedules,
        description,
        category = {},
        videoUrl,
    } = course;

    const filteredSchedules = course.schedules
        ? course.schedules.filter(schedule => {
              const today = new Date();
              const todayString = today.toISOString();
              if (schedule) {
                  if (schedule.end_date) {
                      return schedule.end_date > todayString;
                  }

                  if (schedule.start_date) {
                      return schedule.start_date > todayString;
                  }
              }
              return false;
          })
        : [];

    const metaDescription = cleanString(courseRaw.description);

    return (
        <div className={styles.wrapper} key={key}>
            <Head>
                <title>
                    {`${title || ''} | ${(category && translate(category, 'title')) || ''} | ${t(
                        'meta.title.suffix',
                    )}`}
                </title>
                <meta name="description" content={metaDescription} />
                <meta name="Description" content={metaDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={course.image} />
                <meta property="og:image:width" content="600" />
                <meta property="og:image:height" content="450" />
                <meta property="og:locale" content={locale} />
            </Head>

            {!!canEdit && (
                <>
                    <ToolBar create />

                    {!!saving && (
                        <div className="editor-saving">
                            <Loading full />
                        </div>
                    )}
                </>
            )}

            <div className={styles.cover}>
                <img src="/static/images/frontpage/section-illustrations.png" alt="background" />
                <img src="/static/images/frontpage/section-illustrations.png" alt="background" />
                <Picture src={course.image} className={styles.image} alt="cover" />
            </div>

            {/* <Container> */}
            <Header
                course={course}
                canEdit={canEdit}
                shareUrl={shareUrl}
                schedules={filteredSchedules}
                setRegister={setRegister}
                selectedSchedule={selectedSchedule}
                setSelectedSchedule={setSelectedSchedule}
            />
            {/* </Container> */}

            <Container>
                <Info
                    description={description}
                    content={content}
                    learn={learn}
                    skills={skills}
                    perks={perks}
                    course={course}
                    canEdit={canEdit}
                    instructions={instructions}
                />
            </Container>

            {(!!videoUrl && (
                <Container>
                    <div className={styles.editor}>
                        <InlineEditor
                            label="videoUrl"
                            type="text"
                            value={videoUrl}
                            settings={{
                                placeholder: t('school.edit.video.placeholder'),
                            }}
                            renderLeft={() => (
                                <span className={styles.videoLeft}>
                                    {t('school.edit.video.upload')}
                                </span>
                            )}
                        />
                    </div>
                    <div className={styles.playerContainer}>
                        {videoLoading ? <Loading className={styles.loading} /> : null}
                        <VideoPlayer
                            className={styles.player}
                            url={videoUrl}
                            onReady={() => {
                                setVideoLoading(false);
                            }}
                        />
                    </div>
                </Container>
            )) ||
                (editMode && (
                    <Container>
                        <InlineEditor
                            label="videoUrl"
                            type="text"
                            value={videoUrl}
                            settings={{
                                placeholder: t('school.edit.video.placeholder'),
                            }}
                            renderLeft={() => (
                                <span className={styles.videoLeft}>
                                    {t('school.edit.video.upload')}
                                </span>
                            )}
                        />
                    </Container>
                ))}

            <Container>
                {(!!filteredSchedules.length || editMode) && (
                    <Element name="schedule">
                        <TitleContent
                            title={t('course.details.content.schedule')}
                            content={
                                <Schedule
                                    schedules={filteredSchedules}
                                    allSchedules={schedules}
                                    setRegister={setRegister}
                                    selectedSchedule={selectedSchedule}
                                    setSelectedSchedule={setSelectedSchedule}
                                />
                            }
                        />
                    </Element>
                )}

                {!!register && !create && !editMode && !course.requestUrl && (
                    <Element name="register">
                        <Register
                            course={course}
                            setRegister={setRegister}
                            orderCourse={orderCourse}
                            orderLoading={orderLoading}
                            selectedSchedule={selectedSchedule}
                            setSelectedSchedule={setSelectedSchedule}
                        />
                    </Element>
                )}
            </Container>

            {!editMode && mapShow && (
                <Container>
                    <SchoolInfo school={school} schoolGalleryHidden={edited.schoolGalleryHidden} />
                </Container>
            )}

            {!editMode && !!blog && !!blog.shortId && mapShow && (
                <Container>
                    <TitleContent
                        title={<CardListHeader title={t('school.blog.title')} link={false} />}
                        content={<Blog blog={blog} />}
                    />
                </Container>
            )}

            {!editMode && !!reviews.length && (
                <Container>
                    <TitleContent
                        title={t('details.reviews.title')}
                        content={<Reviews loading={loading} reviews={reviews} />}
                    />
                </Container>
            )}

            <div>
                {!editMode &&
                    !!course &&
                    !!location &&
                    !!Object.keys(location).length &&
                    !!location.lat &&
                    !!location.lng &&
                    !!school &&
                    !!Object.keys(school).length && (
                        <Container>
                            <Element name="map">
                                <TitleContent
                                    title={t('course.details.content.map')}
                                    content={
                                        mapShow && (
                                            <Map
                                                location={location}
                                                school={school}
                                                show={courseRaw.showAddress}
                                                isStaticMap
                                            />
                                        )
                                    }
                                />
                            </Element>
                        </Container>
                    )}
            </div>

            {!mapShow && <div ref={firstRef} style={{ height: '200px', width: '100%' }} />}

            {/* {!editMode && !!otherCourses && !!otherCourses.length && mapShow && (
                <Container>
                    <TitleContent
                        title={
                            <CardListHeader
                                title={t('course.details.content.course_suggestions')}
                                linkOptions={{
                                    route: 'search',
                                    queryParams: {
                                        school: school.shortId,
                                    },
                                }}
                            />
                        }
                        content={
                            <CardList
                                data={otherCourses}
                                type="course"
                                noButton
                                reference="sameSchool"
                            />
                        }
                    />
                </Container>
            )} */}

            {!editMode && !!relatedCourses && !!relatedCourses.length && mapShow && (
                <Container>
                    <TitleContent
                        title={
                            <CardListHeader
                                title={t('course.courses.related')}
                                linkOptions={{
                                    route: 'search',
                                    queryParams: {
                                        category:
                                            courseRaw && courseRaw.category
                                                ? courseRaw.category.label
                                                : null,
                                        language:
                                            courseRaw && courseRaw.language
                                                ? courseRaw.language.Name
                                                : null,
                                    },
                                }}
                            />
                        }
                        content={
                            <CardList
                                data={relatedCourses}
                                type="course"
                                noButton
                                reference="similarCourses"
                            />
                        }
                    />
                </Container>
            )}

            {!create && !!course && !!course.price && <JsonLD data={course} />}
        </div>
    );
};

Course.propTypes = {
    load: PropTypes.bool,
    datas: PropTypes.object,
    create: PropTypes.bool,
};

Course.defaultProps = {
    datas: {},
    load: false,
    create: false,
};

Course.getInitialProps = async ({ res, query }, { locationProps }) => {
    const { locations = [] } = locationProps || {};

    if (process.browser) {
        return {
            load: true,
        };
    }

    const datas = query.id
        ? await getCourseByShortId(query.id, {
              countries: locations
                  .filter(location => location.default)
                  .map(location => location.country),
          })
        : null;

    if (!datas || !datas.course) {
        res.statusCode = 404;
    }

    return {
        datas,
        load: false,
    };
};

export default Course;
